import React, { useMemo, useState } from 'react'
import { Dropdown, Spin } from 'antd'
import { CaretDown, ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { infoMessage } from 'api/global'
import {
  useGetLeagueParticipants,
  LeagueParticipant,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { AttendeeRecord, leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { Person } from 'types/globalTypes'
import AttendanceFilters from './AttendanceFilters'
import AttendanceTable from './AttendanceTable'
import ManageAttendeesModal from './Modals/ManageAttendeesModal'
import ManageAttendeeModal from './Modals/ManageAttendeeModal'
import CheckInModal from './Modals/CheckInModal'
import CheckOutModal from './Modals/CheckOutModal'
import AddDropInModal from './Modals/AddDropInModal'
import DropInDetailModal from './Modals/DropInDetailModal'
import LeagueCheckInOutModal from './CheckInOut/LeagueCheckInOutModal'
import LeagueRollCallModal from './Modals/LeagueRollCallModal'
import LeagueRollCallHistoryModal from './Modals/LeagueRollCallHistoryModal'
import LeagueTicketsModal from './Modals/LeagueTicketsModal'
import ContactParentsModal from './Modals/ContactParentsModal'
import '../../styles/attendancetab.scss'
import type { MenuProps } from 'antd'

export type { AttendeeRecord }

const PAGE_SIZE = 10

const fmtCheckInDate = (date: string | null | undefined): string => {
  if (!date) return '—'
  return new Date(date).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const calculateAge = (dob: string): number => {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
  return age
}

const mapParticipant = (
  p: LeagueParticipant,
  participantType: AttendeeRecord['participantType'],
): AttendeeRecord => ({
  key: p._id,
  _id: p._id,
  name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—',
  gender: p.gender,
  dob: p.dob,
  familyId: p.familyId,
  organization: p.todayLeagueTeams?.[0]?.organization?.name || '—',
  team: p.todayLeagueTeams?.[0]?.team?.name
    || (p.teamType === 'home' ? p.game?.homeTeamId?.team?.name : p.game?.awayTeamId?.team?.name)
    || '—',
  division: p.todayLeagueTeams?.[0]?.division?.name || p.division?.name || '—',
  checkInDate: fmtCheckInDate(p.checkedInAt),
  hasSubstitute: (p.todayLeagueTeams?.length ?? 0) > 1,
  substituteTeam: p.todayLeagueTeams?.[1]?.team?.name,
  checkInId: p.checkInId ?? null,
  checkInStatus: p.checkInStatus || 'no-status',
  checkOutStatus: p.checkOutStatus ?? null,
  participantType,
})

const AttendanceTab: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''
  const [currentPage, setCurrentPage] = useState(1)

  const {
    checkInOutModalOpen,
    setCheckInOutModalOpen,
    checkInModal,
    checkOutModal,
    addDropInModalOpen,
    setAddDropInModalOpen,
    dropInDetailModalOpen,
    rollCallModalOpen,
    setRollCallModalOpen,
    rollCallHistoryModalOpen,
    setRollCallHistoryModalOpen,
    ticketsModalOpen,
  } = leagueAttendanceStore(store => ({
    checkInOutModalOpen: store.checkInOutModalOpen,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    checkInModal: store.checkInModal,
    checkOutModal: store.checkOutModal,
    addDropInModalOpen: store.addDropInModalOpen,
    setAddDropInModalOpen: store.setAddDropInModalOpen,
    dropInDetailModalOpen: store.dropInDetailModalOpen,
    rollCallModalOpen: store.rollCallModalOpen,
    setRollCallModalOpen: store.setRollCallModalOpen,
    rollCallHistoryModalOpen: store.rollCallHistoryModalOpen,
    setRollCallHistoryModalOpen: store.setRollCallHistoryModalOpen,
    ticketsModalOpen: store.ticketsModalOpen,
  }))

  // filter state
  const [genderFilter, setGenderFilter] = useState<'male' | 'female' | undefined>(undefined)
  const [minAge, setMinAge] = useState<number | undefined>(undefined)
  const [maxAge, setMaxAge] = useState<number | undefined>(undefined)
  const [orgFilter, setOrgFilter] = useState<string | undefined>(undefined)
  const [divisionFilter, setDivisionFilter] = useState<string | undefined>(undefined)
  const [teamFilter, setTeamFilter] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }, [])

  const { data, isLoading } = useGetLeagueParticipants({
    leagueId: leagueId!,
    date: today,
  })

  // flat raw list — tagged with participantType so status labels can build correct check-in payloads
  const rawParticipants: (LeagueParticipant & { _pType: AttendeeRecord['participantType'] })[] = useMemo(() => [
    ...(data?.kids || []).map(p => ({ ...p, _pType: 'kid' as const })),
    ...(data?.guardians || []).map(p => ({ ...p, _pType: 'guardian' as const })),
    ...(data?.authorizedAdults || []).map(p => ({ ...p, _pType: 'authorizedAdult' as const })),
    ...(data?.dropinAttendees || []).map(p => ({ ...p, _pType: 'dropInAttendee' as const })),
  ], [data])

  // Person[] for Contact Parents — kids, guardians, authorized adults (drop-ins excluded, no family link)
  const contactParticipants: Person[] = useMemo(() => [
    ...(data?.kids || []).map(p => ({
      _id: p._id, firstName: p.firstName, lastName: p.lastName,
      gender: (p.gender || 'male') as 'male' | 'female', dob: p.dob || '',
      photoId: '', photo: null, role: 'kid',
    })),
    ...(data?.guardians || []).map(p => ({
      _id: p._id, firstName: p.firstName, lastName: p.lastName,
      gender: (p.gender || 'male') as 'male' | 'female', dob: p.dob || '',
      photoId: '', photo: null, role: 'guardian',
    })),
    ...(data?.authorizedAdults || []).map(p => ({
      _id: p._id, firstName: p.firstName, lastName: p.lastName,
      gender: (p.gender || 'male') as 'male' | 'female', dob: p.dob || '',
      photoId: '', photo: null, role: 'authorizedAdult',
    })),
  ], [data])

  // unique option lists derived from full unfiltered data
  const orgOptions = useMemo(() => {
    const seen = new Set<string>()
    rawParticipants.forEach(p => {
      const v = p.todayLeagueTeams?.[0]?.organization?.name
      if (v && v !== '—') seen.add(v)
    })
    return Array.from(seen).sort()
  }, [rawParticipants])

  const divisionOptions = useMemo(() => {
    const seen = new Set<string>()
    rawParticipants.forEach(p => {
      const v = p.todayLeagueTeams?.[0]?.division?.name
      if (v && v !== '—') seen.add(v)
    })
    return Array.from(seen).sort()
  }, [rawParticipants])

  const teamOptions = useMemo(() => {
    const seen = new Set<string>()
    rawParticipants.forEach(p => {
      const v = p.todayLeagueTeams?.[0]?.team?.name
      if (v && v !== '—') seen.add(v)
    })
    return Array.from(seen).sort()
  }, [rawParticipants])

  // filtered + sorted records
  const allParticipants: AttendeeRecord[] = useMemo(() => {
    const filtered = rawParticipants.filter(p => {
      if (genderFilter && p.gender !== genderFilter) return false
      if (orgFilter) {
        const org = p.todayLeagueTeams?.[0]?.organization?.name
        if (org !== orgFilter) return false
      }
      if (divisionFilter) {
        const div = p.todayLeagueTeams?.[0]?.division?.name
        if (div !== divisionFilter) return false
      }
      if (teamFilter) {
        const team = p.todayLeagueTeams?.[0]?.team?.name
        if (team !== teamFilter) return false
      }
      if (minAge !== undefined || maxAge !== undefined) {
        if (!p.dob) return false
        const age = calculateAge(p.dob)
        if (minAge !== undefined && age < minAge) return false
        if (maxAge !== undefined && age > maxAge) return false
      }
      return true
    })

    const records = filtered.map(p => mapParticipant(p, p._pType))
    records.sort((a, b) => {
      const cmp = a.name.localeCompare(b.name)
      return sortDir === 'asc' ? cmp : -cmp
    })
    return records
  }, [rawParticipants, genderFilter, orgFilter, divisionFilter, teamFilter, minAge, maxAge, sortDir])

  const totalPages = Math.max(1, Math.ceil(allParticipants.length / PAGE_SIZE))
  const pageData = allParticipants.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  const moreItems: MenuProps['items'] = [
    {
      key: 'roll-call-history',
      label: 'View roll-calls',
      onClick: () => setRollCallHistoryModalOpen(true),
    },
    {
      key: 'settings',
      label: 'Settings',
      onClick: () => infoMessage('Settings — coming soon'),
    },
    {
      key: 'print',
      label: 'Print list',
      onClick: () => infoMessage('Print list — coming soon'),
    },
  ]

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  const handleAgeChange = (min: number | undefined, max: number | undefined) => {
    setMinAge(min)
    setMaxAge(max)
    setCurrentPage(1)
  }

  const handleGenderChange = (v: 'male' | 'female' | undefined) => { setGenderFilter(v); setCurrentPage(1) }
  const handleOrgChange = (v: string | undefined) => { setOrgFilter(v); setCurrentPage(1) }
  const handleDivisionChange = (v: string | undefined) => { setDivisionFilter(v); setCurrentPage(1) }
  const handleTeamChange = (v: string | undefined) => { setTeamFilter(v); setCurrentPage(1) }

  return (
    <div className="att-tab-container">
      {/* Top action bar */}
      <div className="att-top-bar">
        <button
          className="att-checkinout-btn"
          onClick={() => setCheckInOutModalOpen(true)}
        >
          Check-in/out
        </button>
        <button
          className="att-checkinout-btn"
          onClick={() => setAddDropInModalOpen(true)}
        >
          Add drop-in
        </button>
        <button
          className="att-checkinout-btn"
          onClick={() => setRollCallModalOpen(true)}
        >
          Roll-call
        </button>
        <ManageAttendeesModal />
        <Dropdown
          menu={{ items: moreItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button className="att-more-btn">
            More <CaretDown size={14} color="#000" />
          </button>
        </Dropdown>
      </div>

      <div className="att-separator" />

      <AttendanceFilters
        genderFilter={genderFilter}
        minAge={minAge}
        maxAge={maxAge}
        orgFilter={orgFilter}
        divisionFilter={divisionFilter}
        teamFilter={teamFilter}
        sortDir={sortDir}
        onGenderChange={handleGenderChange}
        onAgeChange={handleAgeChange}
        onOrgChange={handleOrgChange}
        onDivisionChange={handleDivisionChange}
        onTeamChange={handleTeamChange}
        onSortToggle={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
        orgOptions={orgOptions}
        divisionOptions={divisionOptions}
        teamOptions={teamOptions}
      />

      {isLoading ? (
        <div className="att-loading">
          <Spin />
        </div>
      ) : (
        <AttendanceTable data={pageData} />
      )}

      {/* Pagination — only show when there's data */}
      {allParticipants.length > 0 && (
        <div className="att-pagination">
          <button
            className="att-page-nav"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ArrowLeft size={16} color={currentPage === 1 ? '#ccc' : '#000'} />
            Previous
          </button>

          <div className="att-page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`att-page-num ${page === currentPage ? 'att-page-active' : ''}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            className="att-page-nav"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <ArrowRight
              size={16}
              color={currentPage === totalPages ? '#ccc' : '#000'}
            />
          </button>
        </div>
      )}

      <ManageAttendeeModal />
      {checkInOutModalOpen && <LeagueCheckInOutModal />}
      {checkInModal && <CheckInModal />}
      {checkOutModal && <CheckOutModal />}
      {addDropInModalOpen && <AddDropInModal />}
      {dropInDetailModalOpen && <DropInDetailModal />}
      {rollCallModalOpen && <LeagueRollCallModal />}
      {rollCallHistoryModalOpen && <LeagueRollCallHistoryModal />}
      {ticketsModalOpen && <LeagueTicketsModal />}
      <ContactParentsModal participants={contactParticipants} />
    </div>
  )
}

export default AttendanceTab
